import { createReducer } from '@reduxjs/toolkit';
import type { ShowcaseDesignSystem } from 'components/Modal/DsShowcaseModal';
import type { DesignSystemContent } from 'designSystem/content/types';
import { DsLinkState } from 'hooks/useDsLinkState';
import {
	ComponentCategory,
	DesignSystemSection,
	DsViewPreferences,
	TokenCategory,
} from 'designSystem/types';
import { Point, Rect } from 'dragonmouse-shared/geometry';
import {
	Attachment,
	AuthUser,
	ChatMode,
	DomElement,
	DsPick,
	DsMode,
} from 'dragonmouse-shared/interfaces';
import { MenuItem } from 'menu/MenuItem';
import { ReactNode } from 'react';
import storage, { LocalData, SessionData } from 'storage';
import { DS_EDITOR_PREVIEW_BG_DEFAULT, layout } from 'tokens';
import { TransferBoard } from 'transfer/TransferBoard';
import { UploadTracker } from 'utils';
import {
	activateLastTool,
	addDsTab,
	addToSelected,
	addTrackedUserId,
	cancelTransformAnimation,
	centerCanvasAroundSceneBoundingBox,
	centerCanvasAroundScenePosition,
	clearDesignSystemFilters,
	clearDsPicks,
	doCommand,
	redoCommand,
	removeDsPick,
	removeDsTab,
	removeFromSelected,
	removeTrackedUserId,
	resetOffset,
	setActiveDesignSystemId,
	setActiveDsChatId,
	setActiveDsTab,
	setActiveLeftPanelTab,
	setActiveSceneChatId,
	setActiveSceneId,
	setActiveTool,
	setAuthUser,
	setUserReady,
	setCanvasHeight,
	setCanvasMousePosition,
	setCanvasWidth,
	setCenteredFrameId,
	setCenterFramesAsChatGeneratesThem,
	setChatDesignSystemMode,
	setChatReferenceText,
	setClipboard,
	setDarkModeBehavior,
	setDesignSystemChatPanelWidth,
	setDesignSystemContent,
	setActiveDsMode,
	setDesignSystemFilterCategories,
	setDeviceConfig,
	setDsTabs,
	setDsTabScrollPosition,
	setDsViewPreference,
	setFrameCentered,
	setHovered,
	setPlanPricingModalVisible,
	setPlanPricingModalPending,
	setInspectorDeepThinking,
	setInspectorLeftPanelWidth,
	setInspectorRightPanelWidth,
	setIsDragging,
	setLeftPanelWidth,
	setMergeInstruction,
	setMouseDragMode,
	setOffset,
	setPanelsVisible,
	setPendingChatInput,
	setPendingDsChatInput,
	setPrototypeScaling,
	setRightPanelWidth,
	setScale,
	setScaleAroundMousePosition,
	setSelected,
	setSelectedDomElements,
	setSettings,
	setSettingsModalVisible,
	setSidebarDocked,
	setSidebarHovered,
	setSidebarVisible,
	setSystemDarkMode,
	setToolModalMousePosition,
	setToolModified,
	setUpdateNavigatorScrollPosition,
	setVisibleToolModal,
	setZoomPreviewBox,
	showAlertModal,
	showConfirmModal,
	showContextMenu,
	showPromptModal,
	showDsShowcaseModal,
	toggleDesignSystemFilterCategory,
	toggleDsPick,
	transformCanvasToShowSceneBoundingBox,
	undoCommand,
	updateTransformDuringAnimation,
	zoomAroundCanvasCenter,
	zoomAroundMousePosition,
	zoomSetAroundCanvasCenter,
	optimisticallyUpdateDsFiles,
	resolveOptimisticDsFileWrites,
	updateDsTab,
	addDsBreakageNotification,
	dismissDsBanner,
	dismissDsBreakageNotification,
	clearDsBreakageNotifications,
	resolveBreakageSymbols,
	setActiveCommentId,
	setHoveredCommentId,
	setCommentReorderPanelExpanded,
	addCommentIntroShownForScene,
	setNewVersionAvailable,
	setHistoricalDsVersionNumber,
} from './actions';
import type { DsFileOptimisticWrite } from './actions';
import { Pick } from './classes';
import { Command } from './commands';
import {
	getOffsetToPreservePositionWhenScaling,
	openCanvasHeight,
	openCanvasLeft,
	openCanvasWidth,
	updateScale,
} from './util';
import { createDesignSystemContentFromFiles } from 'designSystem/content/factory';
import { applyDsFileWrites } from './util/dsFiles';

export enum ToolType {
	SELECT = 1, // start enum at 1 so that i can say if (toolType) and have it be true only when set
	HAND,
	ZOOM,
	CHAT,
}

export enum ZoomType {
	ZOOM_IN = 1,
	ZOOM_OUT,
}

export enum DarkModeBehavior {
	SYSTEM = 'Follow system',
	ON = 'Dark mode',
	OFF = 'Light mode',
}

export type DeviceKind = 'mobile' | 'desktop';
export type DeviceInput = 'touch' | 'mouse';

export interface DeviceConfig {
	kind: DeviceKind;
	input: DeviceInput;
}

export interface Settings {
	darkModeBehavior: DarkModeBehavior;
	figmaIntegrationEnabled: boolean;
	connectingLinesEnabled: boolean;
	resizeFramesEnabled: boolean;
	createOrganizationEnabled: boolean;
	// visualEditsEnabled moved to account-level settings (user.metadata.settings)
}

export type ContextMenu = {
	point: Point;
	menuItems: MenuItem[];
};

export type PendingChatInput = {
	frameIds: string[];
	message: string; // funny. this should be called prompt i think ;)
	attachments?: Attachment[];
	loadingAttachments?: UploadTracker[];
	chatMode: ChatMode;
};

export type PendingDsChatInput = {
	frameIds?: string[];
	message: string;
	attachments?: Attachment[];
	loadingAttachments?: UploadTracker[];
	chatMode: ChatMode;
};

export interface ConfirmModalOptions {
	title: string;
	description: string | ReactNode;
	cancelLabel?: string;
	submitLabel?: string;
	altActionLabel?: string;
	onCancel?: () => void;
	onSubmit: () => void | Promise<void>;
	onAltAction?: () => void;
	onClickOutside?: (e: { preventDefault: () => void }) => void;
}

export interface DsShowcaseModalOptions {
	onSelect: (item: ShowcaseDesignSystem) => void;
}

export interface AlertModalOptions {
	title: string;
	description: string | ReactNode;
	submitLabel?: string;
	onSubmit: () => void;
	onClickOutside?: (e: { preventDefault: () => void }) => void;
}

export interface PromptModalOptions {
	title: string;
	description?: string | ReactNode;
	cancelLabel?: string;
	submitLabel: string;
	placeholder?: string;
	children?: ReactNode;
	disabled?: (value: string) => boolean;
	isMultilineInput?: boolean;
	onCancel?: () => void;
	onSubmit: (value?: string) => void;
	// takes in an entered input, if it's invalid, returns a string describing the error
	// if it's valid, returns null
	// if not set, all inputs are valid
	validate?: (value: string) => string | null;

	// the value the input should display when opened (if any)
	initialValue?: string;

	// as the user types, this function will be called to pre-process their input before it's shown to them
	filter?: (value: string) => string;
}

export interface DsBreakageNotification {
	id: string;
	designSystemId: string;
	removedSymbols: string[];
	impacts: Array<{ source: string; target: string }>;
	timestamp: number;
	undoFiles: Array<{ path: string; content: string }>;
	displayNames: Record<string, string>;
}

export enum MouseDragMode {
	NONE,
	SELECT_TOOL,
	UI,
}

export enum LeftPanelTab {
	CHAT = 'Chat',
	DOCUMENT = 'Frames',
	DESIGN_SYSTEM = 'Design System',
}

export interface DsTab {
	id: string; // Unique identifier
	section: DesignSystemSection; // Which section this tab shows
	contentPath?: string; // Optional path to specific content within section (e.g., "guidelines/accessibility.md")
	label: string; // Display name
	showBackButton?: boolean; // IF true, on the page show a back button leading to the list view for this section
}

// when rendering the scene to the canvas window, each scene coordinate is transformed
// to generate a point on the canvas like so:
//   canvas.x = (scale * scene.x) + offset.x
//   canvas.y = (scale * scene.y) + offset.y
//
// note that the canvas coordinates are oriented based on how the browser does things
// (0, 0) is in the top-left, +x is to the right, and +y is down
//
// if the scale is 1.0, the user is seeing the scene at its default size in the browser ("100%")
export interface SceneTransform {
	scale: number;
	offset: Point;
}

export interface TransformAnimation {
	startTime: number;
	duration: number;
	startTransform: SceneTransform;
	endTransform: SceneTransform;
	easing: (t: number) => number;
}

export type Scaling = 'mockup' | 'actual-size';

export interface Store {
	activeTool: ToolType;
	toolModified: boolean; // this is true when the modifier key (Option/Alt) is pressed
	lastTool: ToolType; // the prior tool (useful for remembering the baseline tool when the user hops into a temporary tool mode)
	isDragging: boolean; // when true, blocks mouse events from reaching the iframe

	// if a transform has been initialized for a scene, it is stored here
	transforms: { [sceneId: string]: SceneTransform };

	// animation state for transforms
	transformAnimation: TransformAnimation | null;

	// the position of the mouse in canvas coordinates
	canvasMousePosition: Point;

	// a box previewing a canvas area that may soon be zoomed in on
	// when null, no such preview operation is being performed
	// in canvas coordinates
	zoomPreviewBox: Rect | null;

	// the dimensions of the canvas windowwhich lies *under* the various panels
	// these do not change when the side panels/etc are hidden/shown
	// NOTE: I thought about using a point to represent these, but that's not semantic
	//       I also thought about using a rect, but the x/y would always be 0/0 and thus not
	//       meaningful. I'm open to bright ideas as to how to bundle these into one variable cleanly - ss
	// at various points (e.g. during initialization), we don't know what the canvas Width/Height is, so we use null
	canvasWidth: number | null;
	canvasHeight: number | null;

	// if true, show the left/right panel and similar UI elements
	panelsVisible: boolean;

	// if true, show the global sidebar
	sidebarVisible: boolean;

	// if true, the sidebar visibility has been initialized
	// (the first time the browser is loaded, we make some decisions as to whether or not to show the sidebar)
	sidebarVisibilityInitialized: boolean;

	// if true, sidebar is docked (takes up space) - desktop only
	sidebarDocked: boolean;

	// if true, sidebar/menu button is hovered (for transient visibility) - desktop only
	sidebarHovered: boolean;

	leftPanelWidths: { [tab in LeftPanelTab]: number };
	rightPanelWidth: number;
	inspectorRightPanelWidth: number;
	inspectorLeftPanelWidth: number;
	designSystemChatPanelWidth: number;
	inspectorDeepThinking: boolean;
	selectedDomElements: DomElement[]; // the elements that are currently selected in the inspector. putting it here because setting up a context for the frame inspector felt like overkill

	activeLeftPanelTab: LeftPanelTab;

	darkMode: boolean; // this flag controls actual rendering
	systemDarkMode: boolean; // here we remember the system setting
	settings: Settings;

	hovered: Pick | null;
	selected: Pick[];

	// if the user is dragging the mouse around the canvas, this keeps track of who is managing the drag
	// currently this is used to support:
	//  - certain things in our system (e.g. iframes) not reacting to the mouse during a drag
	//  - allowing the SelectTool to be the only consumer of keyboard events during a drag
	mouseDragMode: MouseDragMode;

	// Global context menu. To appear when defined.
	contextMenu: ContextMenu | null;

	// Global prompt. To appear when defined.
	promptModal: PromptModalOptions | null;
	confirmModal: ConfirmModalOptions | null;
	alertModal: AlertModalOptions | null;
	planPricingModalVisible: boolean;
	planPricingModalPending: boolean; // true when plan pricing modal should show on next chat input focus
	settingsModalVisible: boolean;
	dsShowcaseModal: DsShowcaseModalOptions | null;

	// which tool modal is currently visible (if any)
	visibleToolModal: ToolType | null;

	clipboard: TransferBoard | null;

	// when this flag changes, something has happened which may require the navigator
	// to update its scroll position.
	// (currently, "something" is the action of selecting an element. when that element
	// is already selected, nothing else changes in redux so there is no cue to update scroll
	// position.  but the user may have scrolled that element out of view! so this flag is toggled
	// to indicate we may need to refresh the scroll position to ensure the selected item is in view)
	// (this definitely feels like a misuse of redux but it solves a problem for us right now - ss)
	updateNavigatorScrollPosition: boolean;

	// Keep track of the active scene here in redux.
	// The source of truth for this is the URL path, but it's helpful to have this stored here.
	// THIS SHOULD ONLY BE SET AS A SIDE EFFECT OF ROUTE CHANGES
	// If you want to change it, do so indirectly with `navigate()`
	activeSceneId: string | null;
	activeDesignSystemId: string | null;
	activeSceneChatId: string | null;
	activeDsChatId: string | null;

	// Elements within frames, keyed by site id
	frameElements: Record<string, FrameElement[]>;

	// How to display frames in the frame editor
	prototypeScaling: Scaling;

	pendingChatInputs: { [chatId: string]: PendingChatInput };
	pendingDsChatInputs: { [dsChatId: string]: PendingDsChatInput };

	// when the user highlights text in the chat panel, currently we store it here
	chatReferenceText: string | null;

	// null indicates loading
	// if there is no user the entire studio will be redirected away from
	// so for now we can rely on user.email being set
	authUser: AuthUser | null;

	// when this is true, it's safe to start polling the user's profile and scenes
	// (currently once authUser is set AND the user's DB record exists (after initializeUser if needed))
	userReady: boolean;

	// the mouse position that was clicked to trigger the tool modal
	// in scene coordinates
	//
	// if null, no tool modal is visible
	toolModalMousePosition: Point | null;

	// instructions for merging frames
	mergeInstructions: { [frameId: string]: string };

	// Command stack for undo/redo
	commandStack: Command[];
	undoneCommands: Command[];

	centerFramesAsChatGeneratesThem: boolean;

	// the ID of the frame that is currently centered, null if no frame is centered
	centeredFrameId: string | null;

	// scene comment interaction state
	activeCommentId: string | null;
	hoveredCommentId: string | null;
	commentReorderPanelExpanded: boolean;
	commentIntroShownForScenes: string[];

	// users (other than the main logged in user) whose name, email address, etc we want to have available
	trackedUserIds: string[];

	// device configuration for responsive behavior
	deviceConfig: DeviceConfig | null;

	// user's selected mode for new frames (null = default)
	chatDesignSystemMode: string | null;

	// Design system content (processed manifest files)
	designSystemContent: DesignSystemContent | null;

	// When non-null, the user is viewing a historical (read-only) published version
	historicalDsVersionNumber: number | null;

	// Active mode in DS editor (null = default mode)
	activeDsMode: string | null;

	// Optimistic DS file writes (applied before computing designSystemContent)
	optimisticDsFileWrites: DsFileOptimisticWrite[];

	// Design system filters (categories per section)
	designSystemFilters: {
		theme: {
			selectedCategories: TokenCategory[];
		};
		styles: {};
		components: {
			selectedCategories: ComponentCategory[];
		};
		gallery: {};
		assets: {};
	};

	dsTabs: DsTab[];
	activeDsTabId: string | null;
	dsTabScrollPositions: Record<string, number>;

	selectedDsPicks: DsPick[];

	// List of dependencies (e.g. "this button depends on this color") broken
	// by the user in this browseer, while doing manual edits/etc.
	dsBreakageNotifications: DsBreakageNotification[];

	// Per-DS dismissed banner state, scoped by version so dismissals reset on DS updates
	dismissedDsBannerByDs: Record<string, { state: DsLinkState; versionNumber: number }>;

	// Per-DS view preferences (grid/list mode, etc.)
	dsViewPreferences: DsViewPreferences;

	newVersionAvailable: boolean;
}

// Interface for elements within a frame
export interface FrameElement {
	id: string;
	tagName: string;
	bounds: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
}

// initialize our slice of the store to its default value
const initialState: Store = {
	activeTool: ToolType.SELECT,
	toolModified: false,
	lastTool: ToolType.SELECT,
	isDragging: false,
	transforms: {},
	canvasMousePosition: { x: 0, y: 0 },
	canvasHeight: null,
	canvasWidth: null,
	darkMode: true,
	systemDarkMode: false,
	mouseDragMode: MouseDragMode.NONE,
	panelsVisible: true,
	sidebarVisible: false,
	sidebarVisibilityInitialized: false,
	sidebarDocked: false,
	sidebarHovered: false,
	leftPanelWidths: {
		[LeftPanelTab.CHAT]: layout.chatPanelInitialWidth,
		[LeftPanelTab.DOCUMENT]: layout.panelInitialWidth,
		[LeftPanelTab.DESIGN_SYSTEM]: layout.panelInitialWidth,
	},
	rightPanelWidth: layout.panelInitialWidth,
	inspectorRightPanelWidth: layout.panelInitialWidth,
	inspectorLeftPanelWidth: layout.panelInitialWidth,
	inspectorDeepThinking: false,
	selectedDomElements: [],
	settingsModalVisible: false,
	dsShowcaseModal: null,
	selected: [],
	hovered: null,
	contextMenu: null,
	promptModal: null,
	confirmModal: null,
	alertModal: null,
	planPricingModalVisible: false,
	planPricingModalPending: false,
	visibleToolModal: null,
	clipboard: null,
	updateNavigatorScrollPosition: false,
	activeSceneId: null,
	activeDesignSystemId: null,
	activeSceneChatId: null,
	activeDsChatId: null,
	pendingChatInputs: {},
	pendingDsChatInputs: {},
	chatReferenceText: null,
	authUser: null,
	userReady: false,
	toolModalMousePosition: null,
	transformAnimation: null,
	zoomPreviewBox: null,
	mergeInstructions: {},
	settings: {
		darkModeBehavior: DarkModeBehavior.ON,
		figmaIntegrationEnabled: false,
		connectingLinesEnabled: true,
		resizeFramesEnabled: false,
		createOrganizationEnabled: false,
	},
	chatDesignSystemMode: null,
	activeLeftPanelTab: LeftPanelTab.CHAT,
	frameElements: {},
	prototypeScaling: 'mockup',
	commandStack: [],
	undoneCommands: [],
	centerFramesAsChatGeneratesThem: false,
	centeredFrameId: null,
	activeCommentId: null,
	hoveredCommentId: null,
	commentReorderPanelExpanded: false,
	commentIntroShownForScenes: [],
	trackedUserIds: [],
	deviceConfig: null,
	designSystemChatPanelWidth: layout.panelInitialWidth,
	designSystemContent: null,
	historicalDsVersionNumber: null,
	activeDsMode: null,
	optimisticDsFileWrites: [],
	designSystemFilters: {
		theme: {
			selectedCategories: [],
		},
		styles: {},
		components: {
			selectedCategories: [],
		},
		gallery: {},
		assets: {},
	},
	dsTabs: [],
	activeDsTabId: null,
	dsTabScrollPositions: {},
	selectedDsPicks: [],
	dsBreakageNotifications: [],
	dismissedDsBannerByDs: {},
	dsViewPreferences: {},
	newVersionAvailable: false,
};

const localData = storage.local;
const sessionData = storage.session;

// Deep merge saved state into initialState
if (localData) {
	if (localData.leftPanelWidths) {
		initialState.leftPanelWidths = {
			...initialState.leftPanelWidths,
			...localData.leftPanelWidths,
		};
	}
	if (localData.transforms) {
		initialState.transforms = {
			...initialState.transforms,
			...localData.transforms,
		};
	}
	if (localData.rightPanelWidth !== undefined) {
		initialState.rightPanelWidth = localData.rightPanelWidth;
	}
	if (localData.designSystemChatPanelWidth !== undefined) {
		initialState.designSystemChatPanelWidth = localData.designSystemChatPanelWidth;
	}
	if (localData.settings) {
		initialState.settings = {
			...initialState.settings,
			...localData.settings,
		};
	}
	if (localData.prototypeScaling) {
		initialState.prototypeScaling = localData.prototypeScaling;
	}
	if (localData.sidebarVisible !== undefined) {
		initialState.sidebarVisible = localData.sidebarVisible;
	}
	if (localData.sidebarVisibilityInitialized !== undefined) {
		initialState.sidebarVisibilityInitialized = localData.sidebarVisibilityInitialized;
	}
	if (localData.sidebarDocked !== undefined) {
		initialState.sidebarDocked = localData.sidebarDocked;
	}
	if (localData.designSystemFilters) {
		initialState.designSystemFilters = {
			...initialState.designSystemFilters,
			...localData.designSystemFilters,
		};
	}
}

// Load session-specific state
if (sessionData) {
	if (sessionData.activeSceneChatId !== undefined) {
		initialState.activeSceneChatId = sessionData.activeSceneChatId;
	}
	if (sessionData.activeLeftPanelTab) {
		initialState.activeLeftPanelTab = sessionData.activeLeftPanelTab;
	}
	if (sessionData.commandStack) {
		initialState.commandStack = sessionData.commandStack;
	}
	if (sessionData.undoneCommands) {
		initialState.undoneCommands = sessionData.undoneCommands;
	}
	if (sessionData.centeredFrameId !== undefined) {
		initialState.centeredFrameId = sessionData.centeredFrameId;
	}
	if (sessionData.trackedUserIds) {
		initialState.trackedUserIds = sessionData.trackedUserIds;
	}
	if (sessionData.panelsVisible !== undefined) {
		initialState.panelsVisible = sessionData.panelsVisible;
	}
	if (sessionData.dismissedDsBannerByDs) {
		initialState.dismissedDsBannerByDs = sessionData.dismissedDsBannerByDs;
	}
	if (sessionData.commentIntroShownForScenes) {
		initialState.commentIntroShownForScenes = sessionData.commentIntroShownForScenes;
	}
	if (sessionData.pendingChatInputs) {
		initialState.pendingChatInputs = Object.fromEntries(
			Object.entries(sessionData.pendingChatInputs).map(([id, input]) => [
				id,
				{ ...input, frameIds: input.frameIds || [] },
			])
		);
	}
	if (sessionData.pendingDsChatInputs) {
		initialState.pendingDsChatInputs = sessionData.pendingDsChatInputs;
	}
}

// This selector ensures we always have valid settings with defaults
export const selectSettings = (state: Store): Settings => {
	return state.settings;
};

// Validates if a contentPath exists in the design system content for the given section
const isContentPathValid = (
	content: DesignSystemContent,
	section: DesignSystemSection,
	contentPath: string
): boolean => {
	switch (section) {
		case 'components':
			return !!content.components?.byId?.[contentPath];
		case 'guidelines':
			return !!content.guidelines?.find((g) => g.path === contentPath);
		case 'gallery':
			return !!content.gallery?.find((g) => g.path === contentPath);
		case 'assets':
			return !!content.assets?.all?.find((a) => a.path === contentPath);
		case 'theme':
			return contentPath === 'colors';
		case 'styles':
			// Styles don't support individual content paths yet
			return false;
		default:
			return false;
	}
};

// convenience function to update darkMode when darkModeBehavior changes
const updateDarkModeBehavior = (state: Store, behavior: DarkModeBehavior) => {
	state.settings.darkModeBehavior = behavior;
	switch (behavior) {
		case DarkModeBehavior.SYSTEM:
			state.darkMode = state.systemDarkMode;
			break;
		case DarkModeBehavior.OFF:
			state.darkMode = false;
			break;
		case DarkModeBehavior.ON:
			state.darkMode = true;
			break;
	}
};

const reducer = createReducer(initialState, (builder) => {
	builder
		//------------------------------------------------------------------------------------------------
		// transform-related actions
		//------------------------------------------------------------------------------------------------
		.addCase(setOffset, (state, action) => {
			if (action.payload) {
				state.transformAnimation = null;
				setOffsetSafely(state, action.payload);
				state.centeredFrameId = null;
			}
		})
		.addCase(resetOffset, (state) => {
			if (state.activeSceneId) {
				state.transformAnimation = null;
				state.transforms[state.activeSceneId].offset = {
					x: 0,
					y: 0,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(setScaleAroundMousePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newOffset = getOffsetToPreservePositionWhenScaling(
					state,
					state.canvasMousePosition,
					action.payload
				);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: action.payload,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomAroundCanvasCenter, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newScale = updateScale(state, action.payload);
				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const newOffset = getOffsetToPreservePositionWhenScaling(state, canvasCenter, newScale);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: newScale,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomSetAroundCanvasCenter, (state, action) => {
			if (action.payload !== undefined && state.activeSceneId) {
				const newScale = action.payload;
				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const newOffset = getOffsetToPreservePositionWhenScaling(state, canvasCenter, newScale);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: action.payload,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(zoomAroundMousePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const newScale = updateScale(state, action.payload);
				const newOffset = getOffsetToPreservePositionWhenScaling(
					state,
					state.canvasMousePosition,
					newScale
				);
				state.transformAnimation = null;
				state.transforms[state.activeSceneId] = {
					scale: newScale,
					offset: newOffset,
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(centerCanvasAroundScenePosition, (state, action) => {
			if (action.payload && state.activeSceneId) {
				const transform = selectTransform(state);

				const canvas = {
					x: transform.scale * action.payload.x + transform.offset.x,
					y: transform.scale * action.payload.y + transform.offset.y,
				};

				const canvasCenter: Point = {
					x: state.canvasWidth ? state.canvasWidth / 2 : 0,
					y: state.canvasHeight ? state.canvasHeight / 2 : 0,
				};
				const delta: Point = {
					x: canvasCenter.x - canvas.x,
					y: canvasCenter.y - canvas.y,
				};

				// Start animation to the new transform
				state.transformAnimation = {
					startTime: performance.now(),
					duration: 300,
					startTransform: state.transforms[state.activeSceneId],
					endTransform: {
						scale: transform.scale,
						offset: {
							x: transform.offset.x + delta.x,
							y: transform.offset.y + delta.y,
						},
					},
					easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(centerCanvasAroundSceneBoundingBox, (state, action) => {
			if (action.payload && state.activeSceneId && state.canvasWidth && state.canvasHeight) {
				const { boundingBox, animated, tightFit = false, frameId } = action.payload;

				// Calculate the scale needed to fit the bounding box in the canvas
				// Leave some padding (20% on each side)
				const padding = tightFit ? 0.01 : 0.05;

				const width = openCanvasWidth(state);
				const height = openCanvasHeight(state);

				// prevent division by zero or negative values
				if (boundingBox.width <= 0 || boundingBox.height <= 0) {
					console.error('Invalid bounding box dimensions:', boundingBox);
					return;
				}

				const scaleX = width / (boundingBox.width * (1 + padding * 2));
				const scaleY = height / (boundingBox.height * (1 + padding * 2));

				if (isNaN(scaleX) || isNaN(scaleY)) {
					console.error('Invalid scale calculations:', { scaleX, scaleY });
					return;
				}

				const newScale = Math.min(scaleX, scaleY);

				// Calculate the center point of the bounding box
				const boundingBoxCenter: Point = {
					x: boundingBox.x + boundingBox.width / 2,
					y: boundingBox.y + boundingBox.height / 2,
				};

				// Calculate where this center point would appear on the canvas
				const canvas = {
					x: newScale * boundingBoxCenter.x,
					y: newScale * boundingBoxCenter.y,
				};

				// Calculate the canvas center
				const canvasLeft = openCanvasLeft(state);
				const canvasCenter: Point = {
					x: canvasLeft + width / 2,
					y: height / 2,
				};

				// Calculate the offset needed to center the bounding box
				const delta: Point = {
					x: canvasCenter.x - canvas.x,
					y: canvasCenter.y - canvas.y,
				};

				// for added robustness, initialize this if it doesn't exist
				if (!state.transforms[state.activeSceneId]) {
					state.transforms[state.activeSceneId] = {
						scale: 1,
						offset: { x: 0, y: 0 },
					};
				}

				const newTransform = {
					scale: newScale,
					offset: delta,
				};
				if (animated) {
					state.transformAnimation = {
						startTime: performance.now(),
						duration: 300,
						startTransform: { ...state.transforms[state.activeSceneId] },
						endTransform: newTransform,
						easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
					};
				} else {
					state.transforms[state.activeSceneId] = newTransform;
					state.transformAnimation = null;
				}
				state.centeredFrameId = frameId || null;
			}
		})
		.addCase(transformCanvasToShowSceneBoundingBox, (state, action) => {
			if (action.payload && state.activeSceneId && state.canvasWidth && state.canvasHeight) {
				const boundingBox = action.payload;
				const currentTransform = state.transforms[state.activeSceneId];

				// Calculate the available canvas space
				const canvasLeft = openCanvasLeft(state);
				const availableWidth = openCanvasWidth(state);
				const canvasRight = canvasLeft + availableWidth;

				const canvasTop = 0;
				const availableHeight = openCanvasHeight(state);
				const canvasBottom = canvasTop + availableHeight;

				// Calculate the scale needed to fit the bounding box in the canvas
				// Leave some padding (10% on each side)
				const padding = 0.1;
				const scaleX = availableWidth / (boundingBox.width * (1 + padding * 2));
				const scaleY = availableHeight / (boundingBox.height * (1 + padding * 2));
				const newScale = Math.min(scaleX, scaleY, currentTransform.scale);

				// Recalculate the bounding box canvas coordinates with the new scale
				// This is necessary because the delta calculation needs to be based on the new scale
				const scaledBoundingBoxCanvas = {
					x: newScale * boundingBox.x + currentTransform.offset.x,
					y: newScale * boundingBox.y + currentTransform.offset.y,
					width: newScale * boundingBox.width,
					height: newScale * boundingBox.height,
				};

				// Compute the delta we have to transform by to ensure all edges of the bounding box are visible
				// (if needed -- we may not need to transform at all)
				const delta = { x: 0, y: 0 };
				const spacing = 20;
				if (scaledBoundingBoxCanvas.x < canvasLeft + spacing) {
					delta.x = canvasLeft - scaledBoundingBoxCanvas.x + spacing;
				}
				if (scaledBoundingBoxCanvas.y < canvasTop + spacing) {
					delta.y = canvasTop - scaledBoundingBoxCanvas.y + spacing;
				}
				if (scaledBoundingBoxCanvas.x + scaledBoundingBoxCanvas.width > canvasRight - spacing) {
					delta.x =
						canvasRight - (scaledBoundingBoxCanvas.x + scaledBoundingBoxCanvas.width) - spacing;
				}
				if (scaledBoundingBoxCanvas.y + scaledBoundingBoxCanvas.height > canvasBottom - spacing) {
					delta.y =
						canvasBottom - (scaledBoundingBoxCanvas.y + scaledBoundingBoxCanvas.height) - spacing;
				}

				const currentOffset = currentTransform.offset;

				// Start animation to the new transform
				state.transformAnimation = {
					startTime: performance.now(),
					duration: 300,
					startTransform: currentTransform,
					endTransform: {
						scale: newScale,
						offset: {
							x: currentOffset.x + delta.x,
							y: currentOffset.y + delta.y,
						},
					},
					easing: (t: number) => 1 - Math.pow(1 - t, 3), // easeOutCubic
				};
				state.centeredFrameId = null;
			}
		})
		.addCase(cancelTransformAnimation, (state) => {
			state.transformAnimation = null;
		})
		.addCase(updateTransformDuringAnimation, (state, action) => {
			const { sceneId, transform } = action.payload;
			if (state.activeSceneId === sceneId) {
				state.transforms[sceneId] = transform;
				// noteably this change to the transform does NOT affect frameCentered
				// in practice, this is always triggered by wrappers who decide how to update frameCentered
			}
		})
		//------------------------------------------------------------------------------------------------
		// other actions
		//------------------------------------------------------------------------------------------------
		.addCase(setActiveTool, (state, action) => {
			if (action.payload && action.payload !== state.activeTool) {
				state.activeTool = action.payload;

				// ZOOM and HAND are temporary tools, so we don't remember them as prior tools
				if (state.activeTool !== ToolType.ZOOM && state.activeTool !== ToolType.HAND) {
					state.lastTool = state.activeTool;
				}
			}
		})
		.addCase(setToolModified, (state, action) => {
			if (action.payload !== undefined) {
				state.toolModified = action.payload;
			}
		})
		.addCase(setScale, (state, action) => {
			if (action.payload) {
				setScaleSafely(state, action.payload);
			}
		})
		.addCase(setCanvasMousePosition, (state, action) => {
			if (action.payload) {
				state.canvasMousePosition = action.payload;
			}
		})
		.addCase(setCanvasWidth, (state, action) => {
			if (action.payload !== undefined) {
				state.canvasWidth = action.payload;
			}
		})
		.addCase(setCanvasHeight, (state, action) => {
			if (action.payload !== undefined) {
				state.canvasHeight = action.payload;
			}
		})
		.addCase(setDarkModeBehavior, (state, action) => {
			if (action.payload !== undefined) {
				updateDarkModeBehavior(state, action.payload);
			}
		})
		.addCase(setSystemDarkMode, (state, action) => {
			if (action.payload !== undefined) {
				state.systemDarkMode = action.payload;

				// aggressively update darkMode to update it even when darkModeBehavior is not SYSTEM
				// this is a hack that results in the darkMode being set correctly
				// when we reload the app (since setSystemDarkMode is called during initialization)
				updateDarkModeBehavior(state, state.settings.darkModeBehavior);
				//
				// the old, more conservative code did this:
				// if (state.settings.darkModeBehavior === DarkModeBehavior.SYSTEM) {
				// 	state.darkMode = action.payload;
				// }
			}
		})
		.addCase(activateLastTool, (state) => {
			state.activeTool = state.lastTool;
		})
		.addCase(setSelected, (state, action) => {
			let selected = action.payload || [];
			if (!Array.isArray(selected)) selected = [selected];
			state.selected = selected;

			// clear centeredFrameId (unless selected is of length one and matches it)
			if (!(selected.length === 1 && selected[0].frameId === state.centeredFrameId)) {
				state.centeredFrameId = null;
			}
		})
		.addCase(addToSelected, (state, action) => {
			if (action.payload === null || state.selected.find((pick) => pick.equals(action.payload))) {
				// nothing to do
				return;
			}
			state.selected = state.selected.concat(action.payload);
			// if we have multiple selected frames, neither can be centered
			state.centeredFrameId = null;
		})
		.addCase(removeFromSelected, (state, action) => {
			state.selected = state.selected.filter((pick) => !pick.equals(action.payload));
		})
		.addCase(setHovered, (state, action) => {
			state.hovered = action.payload;
		})
		.addCase(setMouseDragMode, (state, action) => {
			state.mouseDragMode = action.payload || MouseDragMode.NONE;
		})
		.addCase(setPanelsVisible, (state, action) => {
			state.panelsVisible = action.payload;
		})
		.addCase(setSidebarVisible, (state, action) => {
			state.sidebarVisible = action.payload;
			state.sidebarVisibilityInitialized = true;
		})
		.addCase(setSidebarDocked, (state, action) => {
			state.sidebarDocked = action.payload;
		})
		.addCase(setSidebarHovered, (state, action) => {
			state.sidebarHovered = action.payload;
		})
		.addCase(showContextMenu, (state, action) => {
			state.contextMenu = action.payload;
		})
		.addCase(showPromptModal, (state, action) => {
			state.promptModal = action.payload;
		})
		.addCase(showConfirmModal, (state, action) => {
			state.confirmModal = action.payload;
		})
		.addCase(showAlertModal, (state, action) => {
			state.alertModal = action.payload;
		})
		.addCase(setPlanPricingModalVisible, (state, action) => {
			state.planPricingModalVisible = action.payload;
		})
		.addCase(setPlanPricingModalPending, (state, action) => {
			state.planPricingModalPending = action.payload;
		})
		.addCase(setLeftPanelWidth, (state, action) => {
			state.leftPanelWidths = {
				...state.leftPanelWidths,
				[action.payload.tab]: action.payload.width,
			};
		})
		.addCase(setRightPanelWidth, (state, action) => {
			state.rightPanelWidth = action.payload;
		})
		.addCase(setInspectorRightPanelWidth, (state, action) => {
			state.inspectorRightPanelWidth = action.payload;
		})
		.addCase(setInspectorLeftPanelWidth, (state, action) => {
			state.inspectorLeftPanelWidth = action.payload;
		})
		.addCase(setDesignSystemChatPanelWidth, (state, action) => {
			state.designSystemChatPanelWidth = action.payload;
		})
		.addCase(setActiveDsMode, (state, action) => {
			state.activeDsMode = action.payload;
		})
		.addCase(setInspectorDeepThinking, (state, action) => {
			state.inspectorDeepThinking = action.payload;
		})
		.addCase(setSelectedDomElements, (state, action) => {
			state.selectedDomElements = action.payload;
		})
		.addCase(setClipboard, (state, action) => {
			state.clipboard = action.payload;
		})
		.addCase(setUpdateNavigatorScrollPosition, (state, action) => {
			state.updateNavigatorScrollPosition = action.payload;
		})
		.addCase(setSettingsModalVisible, (state, action) => {
			state.settingsModalVisible = action.payload;
		})
		.addCase(showDsShowcaseModal, (state, action) => {
			state.dsShowcaseModal = action.payload;
		})
		.addCase(setDeviceConfig, (state, action) => {
			state.deviceConfig = action.payload;
		})
		.addCase(optimisticallyUpdateDsFiles, (state, action) => {
			const newWrites = action.payload;
			const newIds = newWrites.map((w) => w.id);
			state.optimisticDsFileWrites = [
				...state.optimisticDsFileWrites.filter((w) => !newIds.includes(w.id)),
				...newWrites,
			];

			const baseFiles = state.designSystemContent?.files;
			const versionId = state.designSystemContent?.versionId;
			if (baseFiles && versionId && baseFiles.length > 0) {
				const patchedFiles = applyDsFileWrites(baseFiles, state.optimisticDsFileWrites);
				state.designSystemContent = createDesignSystemContentFromFiles(versionId, patchedFiles);
			}
		})
		.addCase(resolveOptimisticDsFileWrites, (state, action) => {
			const resolvedIds = new Set(action.payload);
			state.optimisticDsFileWrites = state.optimisticDsFileWrites.filter(
				(w) => !resolvedIds.has(w.id)
			);

			// Recompute content from base files + remaining optimistic writes
			const baseFiles = state.designSystemContent?.files;
			const versionId = state.designSystemContent?.versionId;
			if (baseFiles && versionId && baseFiles.length > 0) {
				const patchedFiles = applyDsFileWrites(baseFiles, state.optimisticDsFileWrites);
				state.designSystemContent = createDesignSystemContentFromFiles(versionId, patchedFiles);
			}
		})
		.addCase(setDesignSystemContent, (state, action) => {
			state.designSystemContent = action.payload;

			// Validate tabs - remove any that reference non-existent content
			if (action.payload) {
				const content = action.payload;
				const currentTabs = state.dsTabs;
				const validTabs = currentTabs.filter((tab) => {
					if (!tab.contentPath) return true; // Section-level tabs always valid
					return isContentPathValid(content, tab.section, tab.contentPath);
				});

				if (validTabs.length !== currentTabs.length) {
					state.dsTabs = validTabs;

					// If active tab was removed, pick fallback
					if (!validTabs.find((t) => t.id === state.activeDsTabId)) {
						// Try to find a tab in the same section first
						const removedTab = currentTabs.find((t) => t.id === state.activeDsTabId);
						const sameSectionTab = removedTab
							? validTabs.find((t) => t.section === removedTab.section)
							: null;
						state.activeDsTabId = sameSectionTab?.id ?? validTabs[0]?.id ?? null;
					}
				}
			}
		})
		.addCase(setHistoricalDsVersionNumber, (state, action) => {
			state.historicalDsVersionNumber = action.payload;
		})
		.addCase(setActiveSceneId, (state, action) => {
			if (action.payload !== state.activeSceneId) {
				// Always clear scene-specific state
				state.selected = [];
				state.commandStack = [];
				state.undoneCommands = [];
				state.activeSceneChatId = null;
				state.activeCommentId = null;
				state.hoveredCommentId = null;
				state.activeLeftPanelTab = LeftPanelTab.CHAT;

				state.activeSceneId = action.payload;

				// Restore selection for this scene from session storage
				const sceneId = action.payload;
				if (sceneId) {
					const frameIds = storage.session.selectedFrameIdsByScene?.[sceneId] ?? [];
					state.selected = frameIds.map((id) => new Pick(id));
				}
			}
		})
		.addCase(setActiveDesignSystemId, (state, action) => {
			if (action.payload !== state.activeDesignSystemId) {
				state.activeDesignSystemId = action.payload;

				// Always clear transient DS state
				state.selectedDsPicks = [];
				state.optimisticDsFileWrites = [];
				state.dsBreakageNotifications = [];
				state.historicalDsVersionNumber = null;
				state.activeDsChatId = null;

				// Load tabs, view preferences, and picks for this DS from storage
				const dsId = action.payload;
				if (dsId) {
					const sessionTabs = storage.session.dsTabsByDesignSystem?.[dsId];
					const localTabs = storage.local.dsTabsByDesignSystem?.[dsId];
					const tabState = sessionTabs ?? localTabs ?? { tabs: [], activeTabId: null };
					state.dsTabs = tabState.tabs;
					state.activeDsTabId = tabState.activeTabId;
					state.dsTabScrollPositions = {};
					state.dsViewPreferences = storage.local.dsViewPreferencesByDesignSystem?.[dsId] ?? {};
					state.selectedDsPicks = storage.session.dsPicksByDesignSystem?.[dsId] ?? [];
				} else {
					state.dsTabs = [];
					state.activeDsTabId = null;
					state.dsTabScrollPositions = {};
					state.dsViewPreferences = {};
				}
			}
		})
		.addCase(setActiveSceneChatId, (state, action) => {
			// Reset chat-only mode on the previous chat when switching
			if (state.activeSceneChatId && state.activeSceneChatId !== action.payload) {
				const previousChatInput = state.pendingChatInputs[state.activeSceneChatId];
				if (previousChatInput?.chatMode === ChatMode.CHAT_ONLY) {
					state.pendingChatInputs = {
						...state.pendingChatInputs,
						[state.activeSceneChatId]: {
							...previousChatInput,
							chatMode: ChatMode.DESIGN_AND_CHAT,
						},
					};
				}
			}
			state.activeSceneChatId = action.payload;
		})
		.addCase(setActiveDsChatId, (state, action) => {
			state.activeDsChatId = action.payload;
		})
		.addCase(setPendingChatInput, (state, action) => {
			if (action.payload) {
				state.pendingChatInputs = {
					...state.pendingChatInputs,
					[action.payload.chatId]: action.payload.pendingChatInput,
				};
			}
		})
		.addCase(setPendingDsChatInput, (state, action) => {
			if (action.payload) {
				state.pendingDsChatInputs = {
					...state.pendingDsChatInputs,
					[action.payload.dsChatId]: action.payload.pendingDsChatInput,
				};
			}
		})
		.addCase(setChatReferenceText, (state, action) => {
			state.chatReferenceText = action.payload;
		})
		.addCase(setAuthUser, (state, action) => {
			state.authUser = action.payload;
		})
		.addCase(setUserReady, (state, action) => {
			state.userReady = action.payload;
		})
		.addCase(setToolModalMousePosition, (state, action) => {
			state.toolModalMousePosition = action.payload;
		})
		.addCase(setVisibleToolModal, (state, action) => {
			state.visibleToolModal = action.payload;
		})
		.addCase(setZoomPreviewBox, (state, action) => {
			state.zoomPreviewBox = action.payload;
		})
		.addCase(setSettings, (state, action) => {
			state.settings = {
				...state.settings,
				...action.payload,
			};

			// dark mode needs special handling
			if (action.payload.darkModeBehavior !== undefined) {
				updateDarkModeBehavior(state, action.payload.darkModeBehavior);
			}
		})
		.addCase(setChatDesignSystemMode, (state, action) => {
			state.chatDesignSystemMode = action.payload;
		})
		.addCase(setMergeInstruction, (state, action) => {
			if (action.payload) {
				state.mergeInstructions[action.payload.frameId] = action.payload.instruction;
			}
		})
		.addCase(setActiveLeftPanelTab, (state, action) => {
			state.activeLeftPanelTab = action.payload;
		})
		.addCase(setPrototypeScaling, (state, action) => {
			state.prototypeScaling = action.payload;
		})
		.addCase(doCommand, (state, action) => {
			state.commandStack.push(action.payload);
			state.undoneCommands = [];
		})
		.addCase(undoCommand, (state) => {
			const command = state.commandStack.pop();
			if (command) {
				state.undoneCommands.push(command);
			}
		})
		.addCase(redoCommand, (state) => {
			const command = state.undoneCommands.pop();
			if (command) {
				state.commandStack.push(command);
			}
		})
		.addCase(setCenterFramesAsChatGeneratesThem, (state, action) => {
			state.centerFramesAsChatGeneratesThem = action.payload;
		})
		.addCase(setFrameCentered, (state, action) => {
			state.centeredFrameId = action.payload;
		})
		.addCase(setCenteredFrameId, (state, action) => {
			state.centeredFrameId = action.payload;
		})
		.addCase(addTrackedUserId, (state, action) => {
			if (!state.trackedUserIds.includes(action.payload)) {
				state.trackedUserIds.push(action.payload);
			}
		})
		.addCase(removeTrackedUserId, (state, action) => {
			state.trackedUserIds = state.trackedUserIds.filter((id) => id !== action.payload);
		})
		.addCase(setIsDragging, (state, action) => {
			state.isDragging = action.payload;
		})
		.addCase(setActiveCommentId, (state, action) => {
			state.activeCommentId = action.payload;
		})
		.addCase(addCommentIntroShownForScene, (state, action) => {
			if (!state.commentIntroShownForScenes.includes(action.payload)) {
				state.commentIntroShownForScenes.push(action.payload);
			}
		})
		.addCase(setHoveredCommentId, (state, action) => {
			state.hoveredCommentId = action.payload;
		})
		.addCase(setCommentReorderPanelExpanded, (state, action) => {
			state.commentReorderPanelExpanded = action.payload;
		})
		.addCase(toggleDesignSystemFilterCategory, (state, action) => {
			const { section, category } = action.payload;
			if (section === 'theme') {
				const tokenCategory = category as TokenCategory;
				const selectedCategories = state.designSystemFilters.theme.selectedCategories;
				if (selectedCategories.includes(tokenCategory)) {
					state.designSystemFilters.theme.selectedCategories = selectedCategories.filter(
						(c) => c !== tokenCategory
					);
				} else {
					state.designSystemFilters.theme.selectedCategories.push(tokenCategory);
				}
			} else if (section === 'components') {
				const componentCategory = category as ComponentCategory;
				const selectedCategories = state.designSystemFilters.components.selectedCategories;
				if (selectedCategories.includes(componentCategory)) {
					state.designSystemFilters.components.selectedCategories = selectedCategories.filter(
						(c) => c !== componentCategory
					);
				} else {
					state.designSystemFilters.components.selectedCategories.push(componentCategory);
				}
			}
		})
		.addCase(setDesignSystemFilterCategories, (state, action) => {
			const { section, categories } = action.payload;
			if (section === 'theme' || section === 'components') {
				state.designSystemFilters[section].selectedCategories = categories as any;
			}
		})
		.addCase(clearDesignSystemFilters, (state, action) => {
			const section = action.payload;
			if (section === 'theme') {
				state.designSystemFilters.theme.selectedCategories = [];
			} else if (section === 'components') {
				state.designSystemFilters.components.selectedCategories = [];
			}
		})
		.addCase(addDsTab, (state, action) => {
			const newTab = action.payload;
			// Check if tab already exists (by id)
			const existingIndex = state.dsTabs.findIndex((tab) => tab.id === newTab.id);
			if (existingIndex === -1) {
				state.dsTabs.push(newTab);
			}
			state.activeDsTabId = newTab.id;
		})
		.addCase(updateDsTab, (state, action) => {
			const { id, contentPath, label } = action.payload;
			const tab = state.dsTabs.find((t) => t.id === id);
			if (tab) {
				if (contentPath !== undefined) tab.contentPath = contentPath;
				if (label !== undefined) tab.label = label;
			}
		})
		.addCase(removeDsTab, (state, action) => {
			const tabId = action.payload;
			const tabIndex = state.dsTabs.findIndex((tab) => tab.id === tabId);
			if (tabIndex !== -1) {
				state.dsTabs.splice(tabIndex, 1);
				delete state.dsTabScrollPositions[tabId];
				// If we removed the active tab, activate the previous one (or first remaining)
				if (state.activeDsTabId === tabId) {
					if (state.dsTabs.length > 0) {
						const newActiveIndex = Math.max(0, tabIndex - 1);
						state.activeDsTabId = state.dsTabs[newActiveIndex].id;
					} else {
						state.activeDsTabId = null;
					}
				}
			}
		})
		.addCase(setActiveDsTab, (state, action) => {
			state.activeDsTabId = action.payload;
		})
		.addCase(setDsTabs, (state, action) => {
			state.dsTabs = action.payload;
			// Prune orphaned scroll positions
			const tabIds = new Set(state.dsTabs.map((t) => t.id));
			for (const key of Object.keys(state.dsTabScrollPositions)) {
				if (!tabIds.has(key)) {
					delete state.dsTabScrollPositions[key];
				}
			}
			// If there was an active tab and it's no longer in the new tabs array, default to home (null)
			if (state.activeDsTabId && !state.dsTabs.find((t) => t.id === state.activeDsTabId)) {
				state.activeDsTabId = null;
			}
		})
		.addCase(setDsTabScrollPosition, (state, action) => {
			const { tabId, scrollTop } = action.payload;
			state.dsTabScrollPositions[tabId] = scrollTop;
		})
		.addCase(setDsViewPreference, (state, action) => {
			state.dsViewPreferences = { ...state.dsViewPreferences, ...action.payload };
		})
		// DS editor selection
		.addCase(toggleDsPick, (state, action) => {
			const pick = action.payload;
			const matchIndex = state.selectedDsPicks.findIndex((existing) => {
				if (existing.kind !== pick.kind) return false;
				if (existing.kind === 'token' && pick.kind === 'token')
					return existing.cssVariable === pick.cssVariable;
				if (existing.kind === 'component' && pick.kind === 'component')
					return existing.tagName === pick.tagName;
				if (existing.kind === 'recipe' && pick.kind === 'recipe')
					return existing.className === pick.className;
				if (existing.kind === 'guideline' && pick.kind === 'guideline')
					return existing.guidelinePath === pick.guidelinePath;
				if (existing.kind === 'gallery' && pick.kind === 'gallery')
					return existing.galleryPath === pick.galleryPath;
				if (existing.kind === 'asset' && pick.kind === 'asset')
					return existing.assetPath === pick.assetPath;
				return false;
			});
			if (matchIndex >= 0) {
				state.selectedDsPicks.splice(matchIndex, 1);
			} else {
				state.selectedDsPicks.push(pick);
			}
		})
		.addCase(removeDsPick, (state, action) => {
			const pick = action.payload;
			state.selectedDsPicks = state.selectedDsPicks.filter((existing) => {
				if (existing.kind !== pick.kind) return true;
				if (existing.kind === 'token' && pick.kind === 'token')
					return existing.cssVariable !== pick.cssVariable;
				if (existing.kind === 'component' && pick.kind === 'component')
					return existing.tagName !== pick.tagName;
				if (existing.kind === 'recipe' && pick.kind === 'recipe')
					return existing.className !== pick.className;
				if (existing.kind === 'guideline' && pick.kind === 'guideline')
					return existing.guidelinePath !== pick.guidelinePath;
				if (existing.kind === 'gallery' && pick.kind === 'gallery')
					return existing.galleryPath !== pick.galleryPath;
				if (existing.kind === 'asset' && pick.kind === 'asset')
					return existing.assetPath !== pick.assetPath;
				return true;
			});
		})
		.addCase(clearDsPicks, (state) => {
			state.selectedDsPicks = [];
			if (state.activeDesignSystemId) {
				const existing = storage.session;
				const updated = { ...existing.dsPicksByDesignSystem };
				delete updated[state.activeDesignSystemId];
				storage.session = { ...existing, dsPicksByDesignSystem: updated };
			}
		})
		.addCase(addDsBreakageNotification, (state, action) => {
			state.dsBreakageNotifications.push(action.payload);
		})
		.addCase(dismissDsBreakageNotification, (state, action) => {
			state.dsBreakageNotifications = state.dsBreakageNotifications.filter(
				(n) => n.id !== action.payload
			);
		})
		.addCase(clearDsBreakageNotifications, (state) => {
			state.dsBreakageNotifications = [];
		})
		.addCase(dismissDsBanner, (state, action) => {
			const { dsId, dismissedState, versionNumber } = action.payload;
			state.dismissedDsBannerByDs[dsId] = { state: dismissedState, versionNumber };
		})
		.addCase(resolveBreakageSymbols, (state, action) => {
			const resolved = new Set(action.payload);
			state.dsBreakageNotifications = state.dsBreakageNotifications.filter(
				(n) => !n.removedSymbols.every((s) => resolved.has(s))
			);
		})
		.addCase(setNewVersionAvailable, (state, action) => {
			state.newVersionAvailable = action.payload;
		})
		//------------------------------------------------------------------------------------------------
		// Every time we make a change, save the mode
		//------------------------------------------------------------------------------------------------
		.addMatcher(
			() => true,
			(state) => {
				// in spiderbox, here we called a saveLocalModeIfChanged that only saved
				// when changes happened. i guess this was done to prevent thrashing of localStorage?
				// anyway
				// since it's nontrivial to port this behavior to redux toolkit
				// and we're planning on rethinking how we store state
				// for now let's always save the mode
				// saveLocalMode(state);
				// Anticipate an entire store draft to be passed in.
				// Only take the properties we want to save.

				// Read existing storage first (before creating new objects)
				const existingLocal = storage.local;
				const existingSession = storage.session;

				const localData: LocalData = {
					transforms: state.transforms,
					leftPanelWidths: state.leftPanelWidths,
					rightPanelWidth: state.rightPanelWidth,
					designSystemChatPanelWidth: state.designSystemChatPanelWidth,
					settings: state.settings,
					prototypeScaling: state.prototypeScaling,
					sidebarVisibilityInitialized: state.sidebarVisibilityInitialized,
					sidebarVisible: state.sidebarVisible,
					sidebarDocked: state.sidebarDocked,
					designSystemFilters: state.designSystemFilters,
				};

				// Save tabs to local storage, keyed by DS ID (persistence for future sessions)
				if (state.activeDesignSystemId) {
					const tabState = { tabs: state.dsTabs, activeTabId: state.activeDsTabId };
					localData.dsTabsByDesignSystem = {
						...existingLocal.dsTabsByDesignSystem,
						[state.activeDesignSystemId]: tabState,
					};
					localData.dsViewPreferencesByDesignSystem = {
						...existingLocal.dsViewPreferencesByDesignSystem,
						[state.activeDesignSystemId]: state.dsViewPreferences,
					};
				} else {
					// Preserve existing tabs for other DSs
					localData.dsTabsByDesignSystem = existingLocal.dsTabsByDesignSystem;
					localData.dsViewPreferencesByDesignSystem = existingLocal.dsViewPreferencesByDesignSystem;
				}

				storage.local = localData;

				// Save session-specific state to sessionStorage
				const sessionData: SessionData = {
					activeSceneChatId: state.activeSceneChatId,
					activeLeftPanelTab: state.activeLeftPanelTab,
					commandStack: state.commandStack,
					undoneCommands: state.undoneCommands,
					centeredFrameId: state.centeredFrameId,
					trackedUserIds: state.trackedUserIds,
					panelsVisible: state.panelsVisible,
					commentIntroShownForScenes: state.commentIntroShownForScenes,
				};

				// Save tabs to session storage, keyed by DS ID
				if (state.activeDesignSystemId) {
					const tabState = { tabs: state.dsTabs, activeTabId: state.activeDsTabId };
					sessionData.dsTabsByDesignSystem = {
						...existingSession.dsTabsByDesignSystem,
						[state.activeDesignSystemId]: tabState,
					};
				} else {
					sessionData.dsTabsByDesignSystem = existingSession.dsTabsByDesignSystem;
				}

				// Save DS picks keyed by DS ID
				if (state.activeDesignSystemId) {
					sessionData.dsPicksByDesignSystem = {
						...existingSession.dsPicksByDesignSystem,
						[state.activeDesignSystemId]: state.selectedDsPicks,
					};
				} else {
					sessionData.dsPicksByDesignSystem = existingSession.dsPicksByDesignSystem;
				}

				// Save scene selection keyed by scene ID
				if (state.activeSceneId) {
					sessionData.selectedFrameIdsByScene = {
						...existingSession.selectedFrameIdsByScene,
						[state.activeSceneId]: state.selected.map((p) => p.frameId).filter(Boolean) as string[],
					};
				} else {
					sessionData.selectedFrameIdsByScene = existingSession.selectedFrameIdsByScene;
				}

				sessionData.dismissedDsBannerByDs = state.dismissedDsBannerByDs;

				// Persist chat input drafts, stripping loadingAttachments (non-serializable upload state)
				sessionData.pendingChatInputs = Object.fromEntries(
					Object.entries(state.pendingChatInputs).map(([id, { loadingAttachments, ...rest }]) => [
						id,
						rest,
					])
				);
				sessionData.pendingDsChatInputs = Object.fromEntries(
					Object.entries(state.pendingDsChatInputs).map(([id, { loadingAttachments, ...rest }]) => [
						id,
						rest,
					])
				);

				storage.session = sessionData;
			}
		);
});

export const selectTransform = (state: Store): SceneTransform => {
	if (!state.activeSceneId || !state.transforms[state.activeSceneId]) {
		return {
			scale: 0.25,
			offset: {
				x: layout.panelInitialWidth,
				y: layout.frameHeaderHeight + 1,
			},
		};
	}
	return state.transforms[state.activeSceneId];
};

export const selectTransformInitialized = (state: Store): boolean => {
	if (!state.activeSceneId || !state.transforms[state.activeSceneId]) {
		return false;
	}
	return true;
};

export const selectIsMobile = (state: Store): boolean => {
	return state.deviceConfig?.kind === 'mobile';
};

export const selectDesignSystemChatPanelWidth = (state: Store): number => {
	return state.designSystemChatPanelWidth;
};

export const selectActiveDsMode = (state: Store): string | null => {
	return state.activeDsMode;
};

// Based on the currently active mode, provides various values
export const selectModeResolution = (
	state: Store
): {
	modes: DsMode[] | undefined;
	defaultModeId: string | null;
	activeModeId: string | null;
	activeModeConfig: DsMode | undefined;
	previewBg: string;
} => {
	const config = state.designSystemContent?.config;
	const modes = config?.display?.modes;
	const defaultModeId = modes?.find((t) => t.default)?.id ?? modes?.[0]?.id ?? null;
	const activeModeId = state.activeDsMode ?? defaultModeId;
	const activeModeConfig = modes?.find((t) => t.id === activeModeId);
	const previewBg = activeModeConfig?.bg || config?.display?.bg || DS_EDITOR_PREVIEW_BG_DEFAULT;
	return { modes, defaultModeId, activeModeId, activeModeConfig, previewBg };
};

export const selectActiveDsTab = (state: Store): DsTab | null => {
	if (!state.activeDsTabId) return null;
	return state.dsTabs.find((tab) => tab.id === state.activeDsTabId) || null;
};

export const selectDismissedDsBanner = (
	state: Store,
	dsId: string | undefined,
	versionNumber: number | undefined
): DsLinkState | undefined => {
	if (!dsId) return undefined;
	const dismissed = state.dismissedDsBannerByDs[dsId];
	if (!dismissed || dismissed.versionNumber !== versionNumber) return undefined;
	return dismissed.state;
};

//-------------------------------------------------------
// these utility functions set scale or offset robustly
// when the scene's transform has yet to be initialized, these functions go ahead and initialize it
const setScaleSafely = (state: Store, scale: number) => {
	const sceneId = state.activeSceneId;
	if (sceneId) {
		const currentTransform = selectTransform(state);
		state.transforms[sceneId] = {
			offset: currentTransform.offset,
			scale: scale,
		};
	} else console.error('No active scene id', state);
};
const setOffsetSafely = (state: Store, offset: Point) => {
	const sceneId = state.activeSceneId;
	if (sceneId) {
		const currentTransform = selectTransform(state);
		state.transforms[sceneId] = {
			offset: offset,
			scale: currentTransform.scale,
		};
	} else console.error('No active scene id', state);
};
//-------------------------------------------------------

export default reducer;
